import Button from "@/components/Button";
import { useFontRender } from "@/components/FontRender";
import ModalComponent, { useModal } from "@/components/Modal";
import { useComponent } from "@/hooks/useMicrositeComponent";
import { cn } from "@/lib/utils";
import { IMicrositeComponent, MComponentProfileProps } from "@/types/microsite";
import assetUrl from "@/vars/assetUrl";
import {
  micrositeVerifiedCategorieIcons,
  micrositeVerifiedCategories,
} from "@/vars/micrositeVerifiedCategory";
import { CheckBadgeIcon } from "@heroicons/react/24/solid";
import dayjs from "dayjs";
import dynamic from "next/dynamic";
import { Suspense } from "react";
import { TextComponentStyle } from "./Text";

const RenderIcon = dynamic(() => import("@/components/RenderIcon"), {
  ssr: false,
});

const ProfileComponent = {
  kind: "PROFILE",
  Render: (props) => {
    const {
      microsite,
      isBuilder,
      id,
      data: {
        image,
        imageBackground,
        profileBackground,
        backgroundColor,
        title,
        titleFont,
        titleFontSize,
        titleFontSizeCustom,
        titleIsBold,
        titleIsItalic,
        titleColor,
        description,
        descriptionFont,
        descriptionIsBold,
        descriptionIsItalic,
        descriptionColor,
        profileStyle,
      },
    } = props;
    const verifiedModal = useModal({ isOpen: false });

    const { focusTo } = useComponent();

    const { fontStyle: fontStyleTitle, RenderFontStyle: RenderFontStyleTitle } =
      useFontRender({
        textFont: titleFont,
        style: { color: titleColor ? titleColor : undefined },
      });

    const {
      fontStyle: fontStyleDescription,
      RenderFontStyle: RenderFontStyleDesc,
    } = useFontRender({
      textFont: descriptionFont,
      style: { color: descriptionColor ? descriptionColor : undefined },
    });

    return (
      <>
        {RenderFontStyleTitle}
        {RenderFontStyleDesc}
        {(imageBackground || backgroundColor) && (
          <div className="relative bg-cover bg-no-repeat bg-center -mt-8 pb-[39.02439024390244%] w-full">
            <div
              className="-right-1 -left-1 absolute bg-white bg-cover bg-no-repeat bg-center rounded-b-xl h-full overflow-hidden"
              style={{
                backgroundColor: backgroundColor || undefined,
                backgroundImage: imageBackground
                  ? `url(${assetUrl + imageBackground})`
                  : "",
              }}
            >
              <img
                src={assetUrl + imageBackground}
                className="opacity-0"
                alt="cover image dummy"
              />
            </div>
          </div>
        )}
        <div
          id={`mcomp-${id}`}
          className={cn(
            "relative mx-auto mb-3 w-28",
            (imageBackground || backgroundColor) && "-mt-12 sm:-mt-16"
          )}
        >
          <div
            onClick={() => {
              if (isBuilder) {
                focusTo(id || "");
              }
            }}
            className={cn(
              "flex justify-center items-center bg-gray-200 bg-cover bg-no-repeat bg-center mx-auto w-24 sm:w-28 h-24 sm:h-28 overflow-hidden",
              profileStyle === "square" ? "rounded-md" : "rounded-full"
            )}
            style={{
              backgroundColor: profileBackground || undefined,
            }}
          >
            {image ? (
              <Suspense>
                <RenderIcon icon={image} iconSize="3rem" />
              </Suspense>
            ) : (
              <Suspense>
                {/* <RenderIcon icon="saidi-profile.svg" iconSize="3rem" /> */}
                <RenderIcon icon="default-profile.png" iconSize="3rem" />
              </Suspense>
            )}
          </div>
          {microsite?.extras?.isVerified && (
            <button
              className={cn(
                "absolute bg-white rounded-full w-8 h-8 text-blue-500",
                profileStyle === "square"
                  ? "-right-3 -bottom-3"
                  : "right-0 bottom-0"
              )}
              onClick={verifiedModal.open}
            >
              <CheckBadgeIcon />
            </button>
          )}
        </div>

        <TextComponentStyle
          style={fontStyleTitle}
          position="center"
          textSize={titleFontSize}
          textSizeCustom={titleFontSizeCustom as any}
          componentId={id}
          isBuilder={isBuilder}
        >
          <span
            style={{
              fontWeight: titleIsBold ? "bold" : undefined,
              fontStyle: titleIsItalic ? "italic" : undefined,
            }}
          >
            {title}
          </span>
        </TextComponentStyle>

        <TextComponentStyle
          style={fontStyleDescription}
          position="center"
          componentId={id}
          isBuilder={isBuilder}
        >
          <span
            style={{
              fontWeight: descriptionIsBold ? "bold" : undefined,
              fontStyle: descriptionIsItalic ? "italic" : undefined,
            }}
          >
            {description.split("\n").map((d, i) => (
              <p key={i}>{d}</p>
            ))}
          </span>
        </TextComponentStyle>
        <div className="mt-3 w-full" />
        <ModalComponent
          {...verifiedModal}
          drawer
          className="sm:!max-w-[350px] text-center"
        >
          <div className="bg-white mx-auto -mt-9 mb-2 rounded-full w-12">
            <CheckBadgeIcon className="text-blue-500" />
          </div>
          <h1 className="font-montserrat font-semibold text-xl text-center">
            Verified Status
          </h1>
          <p className="mt-3">This microsite belongs to:</p>
          <p className="font-semibold">
            {microsite?.extras?.verifiedCompanyUrl ? (
              <a
                href={microsite?.extras?.verifiedCompanyUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  if (isBuilder) e.preventDefault();
                }}
                className="underline"
              >
                {microsite?.extras?.verifiedCompany}
              </a>
            ) : (
              <span className="underline">
                {microsite?.extras?.verifiedCompany}
              </span>
            )}
          </p>
          <p className="mt-2">Type:</p>
          <p className="font-semibold">
            {/* @ts-ignore */}
            {
              micrositeVerifiedCategorieIcons[
                microsite?.extras?.verifiedCompanyType as string
              ]
            }
            {
              micrositeVerifiedCategories[
                microsite?.extras?.verifiedCompanyType as string
              ]
            }
          </p>
          <p className="mt-2">Verified At:</p>
          <p className="font-semibold">
            {dayjs(microsite?.extras?.verifiedDate).format("DD MMM YYYY")}
          </p>

          <div className="bg-blue-100 mt-4 p-2 border border-blue-500 rounded-md text-blue-500 text-sm">
            Get verification badge on s.id?{" "}
            <a
              href="https://s.id/verify-microsite"
              target="_blank"
              className="underline"
              rel="noopener noreferrer"
            >
              Learn More
            </a>
          </div>

          <Button
            variant="outline"
            className="mt-4 w-full"
            onClick={verifiedModal.toggle}
          >
            Close
          </Button>
        </ModalComponent>
      </>
    );
  },
} as IMicrositeComponent<MComponentProfileProps>;

export default ProfileComponent;
